import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Grid, Button, TextField, Paper,
  Divider, CircularProgress, Alert,
  Snackbar, Chip, Card, CardContent, Stack, MenuItem
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { fetchPujaById, fetchAllPandits, initiatePayment } from '../../services/epujaService';
import { createBooking } from '../../services/bookingService';
import SEO from '../../components/SEO';
import { PageHero, Stepper as UiStepper } from '../../components/ui';

const steps = ['Puja Details', 'Devotee Information', 'Review & Pay'];

const NAKSHATRA_LIST = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
  'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];

const RASHI_LIST = [
  'Mesh (Aries)', 'Vrishabh (Taurus)', 'Mithun (Gemini)', 'Kark (Cancer)',
  'Singh (Leo)', 'Kanya (Virgo)', 'Tula (Libra)', 'Vrishchik (Scorpio)',
  'Dhanu (Sagittarius)', 'Makar (Capricorn)', 'Kumbh (Aquarius)', 'Meen (Pisces)'
];

function PujaBooking() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get('package');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [puja, setPuja] = useState(null);
  const [pandits, setPandits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({
    packageId: packageId || '',
    panditId: '',
    devoteName: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '',
    gotra: '',
    nakshatra: '',
    rashi: '',
    pujaDateTime: '',
    specialInstructions: '',
    prasadDeliveryAddress: '',
    pujaMode: 'online',
    phone: user?.phoneNumber || '',
    email: user?.email || '',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [pujaData, panditData] = await Promise.all([
          fetchPujaById(id),
          fetchAllPandits({ limit: 20, availableOnline: true }),
        ]);
        setPuja(pujaData);
        setPandits(panditData.data || []);
      } catch (err) {
        setError(t('Failed to load puja details'));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, t]);

  if (loading) return <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Container>;
  if (error || !puja) return (
    <Container sx={{ py: 8, textAlign: 'center' }}>
      <Typography color="error">{error}</Typography>
      <Button onClick={() => navigate('/epuja')} sx={{ mt: 2 }}>{t('Back')}</Button>
    </Container>
  );

  const selectedPackage = puja.packages?.find(p => p.id === formData.packageId);
  const selectedPandit = pandits.find(p => p.id === formData.panditId);
  const totalPrice = selectedPackage ? Number(selectedPackage.price) : Number(puja.basePrice);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      // Submit booking
      try {
        setSubmitting(true);
        setError(null);

        const bookingPayload = {
          type: 'e-pooja',
          pujaId: puja.id,
          pujaPackageId: formData.packageId || undefined,
          panditId: formData.panditId || undefined,
          ePoojaType: puja?.category || puja?.type || 'other',
          ePoojaPerformedFor: formData.devoteName,
          ePoojaDateTime: formData.pujaDateTime || undefined,
          ePoojaSpecialInstructions: formData.specialInstructions || undefined,
          devoteName: formData.devoteName,
          gotra: formData.gotra || undefined,
          nakshatra: formData.nakshatra || undefined,
          rashi: formData.rashi || undefined,
          prasadDeliveryAddress: formData.prasadDeliveryAddress || undefined,
          pujaMode: formData.pujaMode,
          numberOfGuests: 1,
          totalAmount: totalPrice,
          specialRequests: formData.specialInstructions || undefined,
        };

        const booking = await createBooking(bookingPayload);

        // Try to initiate payment
        try {
          const paymentResult = await initiatePayment(booking.id);
          if (paymentResult.redirectUrl) {
            window.location.href = paymentResult.redirectUrl;
            return;
          }
        } catch {
          // Payment initiation failed - show success anyway (booking is created)
        }

        setSnackbarOpen(true);
        setTimeout(() => navigate('/epuja'), 3000);
      } catch (err) {
        const msg = err.response?.data?.message;
        setError(Array.isArray(msg) ? msg.join(', ') : msg || err.message);
      } finally {
        setSubmitting(false);
      }
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const isStepValid = () => {
    switch (activeStep) {
      case 0: return true; // package selection is optional
      case 1: return !!formData.devoteName && !!formData.pujaDateTime;
      case 2: return true;
      default: return false;
    }
  };

  const renderStep = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{t('Select Package')}</Typography>
            </Grid>
            {puja.packages?.sort((a, b) => {
              const o = { basic: 0, standard: 1, premium: 2 };
              return (o[a.tier] || 0) - (o[b.tier] || 0);
            }).map((pkg) => (
              <Grid item xs={12} md={4} key={pkg.id}>
                <Card
                  sx={{
                    cursor: 'pointer', borderRadius: 2,
                    border: formData.packageId === pkg.id ? '2px solid' : '1px solid',
                    borderColor: formData.packageId === pkg.id ? 'primary.main' : 'divider',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => setFormData({ ...formData, packageId: pkg.id })}
                >
                  <CardContent>
                    <Chip label={pkg.tier.toUpperCase()} size="small" color={formData.packageId === pkg.id ? 'primary' : 'default'} sx={{ mb: 1 }} />
                    <Typography variant="h6" fontWeight={600}>{pkg.name}</Typography>
                    <Typography variant="h5" color="primary" fontWeight={700} sx={{ my: 1 }}>
                      ₹{Number(pkg.price).toLocaleString()}
                    </Typography>
                    <Stack spacing={0.3}>
                      {pkg.includes?.map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle sx={{ fontSize: 14, mr: 0.5, color: 'success.main' }} />
                          <Typography variant="caption">{item}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {!formData.packageId && (
              <Grid item xs={12}>
                <Alert severity="info">{t('No package selected - base price')} ₹{Number(puja.basePrice).toLocaleString()} {t('will apply')}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>{t('Puja Mode')}</Typography>
              <Stack direction="row" spacing={2}>
                {['online', 'offline'].map((mode) => (
                  <Chip
                    key={mode}
                    label={t(mode === 'online' ? 'Online (Live Video)' : 'Offline (In-person)')}
                    onClick={() => setFormData({ ...formData, pujaMode: mode })}
                    color={formData.pujaMode === mode ? 'primary' : 'default'}
                    variant={formData.pujaMode === mode ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', px: 2, py: 2.5 }}
                  />
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>{t('Select Pandit (Optional)')}</Typography>
              <TextField
                select fullWidth
                value={formData.panditId}
                onChange={handleChange('panditId')}
                label={t('Choose a Pandit')}
                margin="normal"
              >
                <MenuItem value="">{t('Auto-assign best available pandit')}</MenuItem>
                {pandits.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} - {p.specialization} ({p.rating} ★, {p.experienceYears}yr exp)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{t('Devotee Information for Sankalp')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('This information will be used during the puja sankalp (sacred resolution)')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label={t('Devotee Name (for Sankalp)')} value={formData.devoteName} onChange={handleChange('devoteName')} fullWidth required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('Gotra')} value={formData.gotra} onChange={handleChange('gotra')} fullWidth placeholder={t('e.g., Kashyap, Bharadwaj, Vashishtha')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('Nakshatra')} value={formData.nakshatra} onChange={handleChange('nakshatra')} fullWidth>
                <MenuItem value="">{t('Select Nakshatra')}</MenuItem>
                {NAKSHATRA_LIST.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('Rashi (Moon Sign)')} value={formData.rashi} onChange={handleChange('rashi')} fullWidth>
                <MenuItem value="">{t('Select Rashi')}</MenuItem>
                {RASHI_LIST.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="datetime-local" label={t('Preferred Date & Time')}
                value={formData.pujaDateTime} onChange={handleChange('pujaDateTime')}
                fullWidth required InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('Phone Number')} value={formData.phone} onChange={handleChange('phone')} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('Email')} value={formData.email} onChange={handleChange('email')} fullWidth />
            </Grid>

            {(selectedPackage?.prasadIncluded || puja.prasadDelivery) && (
              <Grid item xs={12}>
                <TextField
                  label={t('Prasad Delivery Address')}
                  value={formData.prasadDeliveryAddress}
                  onChange={handleChange('prasadDeliveryAddress')}
                  fullWidth multiline rows={2}
                  placeholder={t('Full address for prasad delivery')}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label={t('Special Instructions / Sankalp Details')}
                value={formData.specialInstructions}
                onChange={handleChange('specialInstructions')}
                fullWidth multiline rows={3}
                placeholder={t('Any specific wishes or instructions for the puja')}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{t('Booking Summary')}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography color="text.secondary">{t('Puja')}</Typography></Grid>
                  <Grid item xs={6}><Typography fontWeight={600}>{puja.name}</Typography></Grid>

                  {selectedPackage && (
                    <>
                      <Grid item xs={6}><Typography color="text.secondary">{t('Package')}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight={600}>{selectedPackage.name} ({selectedPackage.tier})</Typography></Grid>
                    </>
                  )}

                  <Grid item xs={6}><Typography color="text.secondary">{t('Devotee Name')}</Typography></Grid>
                  <Grid item xs={6}><Typography fontWeight={600}>{formData.devoteName}</Typography></Grid>

                  {formData.gotra && (
                    <>
                      <Grid item xs={6}><Typography color="text.secondary">{t('Gotra')}</Typography></Grid>
                      <Grid item xs={6}><Typography>{formData.gotra}</Typography></Grid>
                    </>
                  )}

                  {formData.nakshatra && (
                    <>
                      <Grid item xs={6}><Typography color="text.secondary">{t('Nakshatra')}</Typography></Grid>
                      <Grid item xs={6}><Typography>{formData.nakshatra}</Typography></Grid>
                    </>
                  )}

                  {formData.pujaDateTime && (
                    <>
                      <Grid item xs={6}><Typography color="text.secondary">{t('Date & Time')}</Typography></Grid>
                      <Grid item xs={6}><Typography>{new Date(formData.pujaDateTime).toLocaleString()}</Typography></Grid>
                    </>
                  )}

                  <Grid item xs={6}><Typography color="text.secondary">{t('Mode')}</Typography></Grid>
                  <Grid item xs={6}><Typography>{formData.pujaMode === 'online' ? t('Online (Live Video)') : t('Offline (In-person)')}</Typography></Grid>

                  {selectedPandit && (
                    <>
                      <Grid item xs={6}><Typography color="text.secondary">{t('Pandit')}</Typography></Grid>
                      <Grid item xs={6}><Typography>{selectedPandit.name}</Typography></Grid>
                    </>
                  )}

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}><Typography variant="h6">{t('Total Amount')}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="h5" color="primary" fontWeight={700}>₹{totalPrice.toLocaleString()}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Alert severity="info">
                {t('After booking, you will be redirected to the payment gateway. Your puja will be confirmed after successful payment.')}
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO title={`${t('Book your puja')} — ${puja.name}`} canonicalUrl={`/epuja/${id}/book`} noindex />
      <PageHero
        compact
        crumbs={[{ label: t('Home'), to: '/' }, { label: t('Puja Booking'), to: '/epuja' }, { label: puja.name, to: `/epuja/${id}` }, { label: t('Booking') }]}
        title={t('Book your puja')}
        subtitle={puja.name}
      />
      <div className="container booking-wrap">
      <Paper className="card booking__panel" sx={{ p: 3, borderRadius: 2 }}>
        <UiStepper steps={steps.map((label) => t(label))} current={activeStep} />

        {renderStep(activeStep)}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {activeStep > 0 && (
              <Button variant="outlined" onClick={handleBack}>{t('Back')}</Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">
              {t('Total')}: <strong>₹{totalPrice.toLocaleString()}</strong>
            </Typography>
            <Button
              variant="contained" size="large"
              onClick={handleNext}
              disabled={!isStepValid() || submitting}
              sx={{ borderRadius: 2, px: 4 }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> :
                activeStep === steps.length - 1 ? t('Book & Pay') : t('Next')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          {t('Puja booking successful! Your reference number has been sent to your email.')}
        </Alert>
      </Snackbar>
      </div>
    </>
  );
}

export default PujaBooking;
